import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import {
  OrganisationTypeLabels,
  ProjectAccessConstants,
  ProjectPermissionConstants,
} from "../../../../../../constants";
import {
  EditSelection,
  NavigationSearchParams,
  Origin,
  PrivateProjectAccessDetailsParams,
  SelectData,
} from "../../../../../../models";
import {
  changeProjectResponsibleOwner,
  resendProjectOrganisationInvitation,
  revokeProjectOrganisationInvitation,
  updateProjectOrganisationInvitation,
} from "../../../../../../service/organisation";
import {
  getProjectOrganisationInvitationDetails,
  GetProjectOrganisationInvitationDetailsResponse,
} from "../../../../../../service/query";
import { ServiceError, Status } from "../../../../../../service/Shared";
import { getErrorMessageFromCode } from "../../../../../../service/ValidationErrorFormatter";
import { useAuth } from "../../../../../../useAuth";
import { getFormattedDate, useIsLoadingWrapper } from "../../../../../../utils";
import {
  getProjectAccessDetailsRoute,
  getProjectAccessRoute,
  getProjectAuditHistoryRoute,
  getProjectDetailsByUuid,
  getSettingsAuditHistoryRoute,
  getSettingsProjectAccessRoute,
} from "../../../../../../utils/routes";
import { Toast } from "../../../../../../widget";
import { ProjectContext } from "../../ProjectContext";

// Both service wrappers return `errors: []` for a failure carrying no structured detail - a dropped
// connection, a gateway 502 - so every call needs a message of its own to fall back on.
const LOAD_ERROR_MESSAGE = "Couldn't load this invitation - please try again later.";
const UPDATE_ACCESS_ERROR_MESSAGE = "Couldn't update the access level - please try again later.";
const REVOKE_ERROR_MESSAGE = "Couldn't revoke this organisation's access - please try again later.";
const RESEND_ERROR_MESSAGE = "Couldn't resend this invitation - please try again later.";
const RESPONSIBLE_OWNER_ERROR_MESSAGE = "Couldn't update the responsible owner - please try again later.";

const toErrorMessage = (errors: ServiceError[] | undefined, fallback: string): string =>
  errors?.length ? getErrorMessageFromCode(errors[0].code) : fallback;

interface UseDetailsReturnData {
  handleBackClick: () => void;
  backLinkText?: string;
  invitationDetails?: GetProjectOrganisationInvitationDetailsResponse;
  organisationType: string;
  dateSent: string;
  accessOptions: SelectData;
  accessSelection: EditSelection;
  onAccessSelectionChange: (value: EditSelection) => void;
  onSubmit: () => void;
  onCancel: () => void;
  onResendInvitation: () => void;
  showResendInvitationModal: boolean;
  onOpenResendInvitationModal: () => void;
  onCloseResendInvitationModal: () => void;
  onRevokeAccess: () => void;
  showRevokeModal: boolean;
  onOpenRevokeAccessModal: () => void;
  onCloseRevokeModal: () => void;
  showAssignResponsibleOwnerModal: boolean;
  currentResponsibleOwnerName?: string;
  onConfirmAssignResponsibleOwner: () => void;
  onCancelAssignResponsibleOwner: () => void;
  canManageAccess: boolean;
  canRevoke: boolean;
  canResend: boolean;
  showMakeResponsibleOwner: boolean;
  isOnSubmitLoading: boolean;
  isLoading: boolean;
  hasLoadError: boolean;
  errors?: ServiceError[];
}

export const useDetails = (): UseDetailsReturnData => {
  const navigate = useNavigate();

  const { currentUserType } = useAuth();

  /*
    projectUuid comes from the route rather than the loaded invitation: the back links have to work
    when the fetch fails, which is exactly when there is no invitation to read it off.
  */
  const { projectOrganisationInvitationUuid, projectUuid } = useParams<PrivateProjectAccessDetailsParams>();
  const [searchParams] = useSearchParams();

  const [invitationDetails, setInvitationDetails] = useState<GetProjectOrganisationInvitationDetailsResponse>();
  const [rowVersion, setRowVersion] = useState<number>();
  const [accessSelection, setAccessSelection] = useState<EditSelection>({ access: null, makeResponsibleOwner: false });
  const [showRevokeModal, setShowRevokeModal] = useState<boolean>(false);
  const [showResendInvitationModal, setShowResendInvitationModal] = useState<boolean>(false);
  const [showAssignResponsibleOwnerModal, setShowAssignResponsibleOwnerModal] = useState<boolean>(false);
  const [backLinkText, setBackLinkText] = useState<string>();
  const [backLink, setBackLink] = useState<string>();
  const [isOnSubmitLoading, setIsOnSubmitLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoadError, setHasLoadError] = useState(false);
  const [errors, setErrors] = useState<ServiceError[]>();
  const { hasProjectPermission, projectDetails, shouldRefreshProjectDetails } = useContext(ProjectContext);

  const getInvitationDetails = useCallback(async (invitationUuid: string) => {
    setIsLoading(true);
    setHasLoadError(false);

    const response = await getProjectOrganisationInvitationDetails({
      projectOrganisationInvitationUuid: invitationUuid,
    });

    // Keyed off the status rather than the presence of errors - without this the page renders
    // nothing at all, so a failure with no structured detail leaves a blank screen and no way back.
    if (response.status !== Status.Success || !response.data) {
      setHasLoadError(true);
      setIsLoading(false);
      Toast.error({ message: toErrorMessage(response.errors, LOAD_ERROR_MESSAGE) });
      return;
    }

    setInvitationDetails(response.data);
    // The stored intent is applied on acceptance and spent thereafter, so only a pending
    // invitation's carries into the checkbox.
    setAccessSelection({
      access: response.data.role,
      makeResponsibleOwner:
        response.data.calculatedStatus === ProjectAccessConstants.STATUS_SENT && response.data.makeResponsibleOwner,
    });
    setRowVersion(response.data.rowVersion);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (projectOrganisationInvitationUuid) {
      getInvitationDetails(projectOrganisationInvitationUuid);
      return;
    }

    // The route always supplies the uuid, but without this the initial isLoading would never clear
    // and the page would sit on a spinner for good.
    setIsLoading(false);
    setHasLoadError(true);
  }, [projectOrganisationInvitationUuid, getInvitationDetails]);

  useEffect(() => {
    // TODO: We could refactor the searchParams here to use the origin identifier
    const isBackToProjectAccessTab = !!searchParams.get(NavigationSearchParams.projectAccessTab);
    if (isBackToProjectAccessTab && projectUuid) {
      setBackLinkText("Back to full project");
      setBackLink(
        hasProjectPermission(ProjectPermissionConstants.READ_PROJECT)
          ? getProjectAccessRoute(projectUuid, currentUserType)
          : getProjectDetailsByUuid(projectUuid, currentUserType)
      );
    }
    const isBackToSettingsProjectAccessTab = !!searchParams.get(NavigationSearchParams.settingsProjectAccessTab);
    if (isBackToSettingsProjectAccessTab) {
      setBackLinkText("Back to project access");
      setBackLink(getSettingsProjectAccessRoute(currentUserType));
    }
    const origin = searchParams.get("origin");
    if (origin === Origin.ProjectAuditPage && projectUuid) {
      setBackLinkText("Back to audit");
      setBackLink(getProjectAuditHistoryRoute(projectUuid, currentUserType));
    }
    if (origin === Origin.OrganisationAuditPage) {
      setBackLinkText("Back to audit");
      setBackLink(getSettingsAuditHistoryRoute(currentUserType));
    }
  }, [searchParams, projectUuid, currentUserType, hasProjectPermission]);

  const handleBackClick = (): void => {
    if (backLink) {
      navigate(backLink);
    }
  };

  // Granted only where is_responsible_owner is true, so this is also the RO-only gate (REQ-018).
  const canManageAccess = hasProjectPermission(ProjectPermissionConstants.MANAGE_PROJECT_ORGANISATION_INVITATION);

  const canRevoke = canManageAccess && ProjectAccessConstants.canRevokeInvitation(invitationDetails?.calculatedStatus);

  const canResend = canManageAccess && ProjectAccessConstants.canResendInvitation(invitationDetails?.calculatedStatus);

  const accessOptions = useMemo<SelectData>(() => {
    const options = ProjectAccessConstants.getInvitationAccessOptions(
      invitationDetails?.targetOrganisation.organisationType
    );

    // An invitation predating the type gate can hold a role the gate no longer offers. It is added
    // back so the field reads truthfully; re-picking it saves nothing, as it matches the stored role.
    if (invitationDetails?.role && !options.some((option) => option.key === invitationDetails.role)) {
      options.push({ key: invitationDetails.role, value: invitationDetails.role });
    }

    return options;
  }, [invitationDetails]);

  const isAccepted = invitationDetails?.calculatedStatus === ProjectAccessConstants.STATUS_ACCEPTED;

  const isPending = invitationDetails?.calculatedStatus === ProjectAccessConstants.STATUS_SENT;

  // What the record already carries, which is what the checkbox was seeded with.
  const storedOwnerIntent = isPending && !!invitationDetails?.makeResponsibleOwner;

  const showMakeResponsibleOwner =
    canManageAccess &&
    // Accepted transfers now, through changeProjectResponsibleOwner; pending transfers on
    // acceptance, through the invitation's own flag (BR-02861). Rejected, expired and revoked
    // records never will.
    (isAccepted || isPending) &&
    !!accessSelection.access &&
    ProjectAccessConstants.RESPONSIBLE_OWNER_ELIGIBLE_ROLES.includes(accessSelection.access) &&
    projectDetails?.responsibleOwner?.uuid !== invitationDetails?.targetOrganisation.uuid;

  const onCloseResendInvitationModal = (): void => {
    setShowResendInvitationModal(false);
  };

  const onOpenResendInvitationModal = (): void => {
    setShowResendInvitationModal(true);
  };

  /*
    A resend issues a new invitation record rather than updating this one, so it navigates to the uuid
    that comes back instead of patching local state the way onRevokeAccess does. The search params are
    carried over to keep the back link pointing wherever the user arrived from.
  */
  const onResendInvitation = async (): Promise<void> => {
    setShowResendInvitationModal(false);
    if (invitationDetails && projectOrganisationInvitationUuid && rowVersion) {
      const response = await resendProjectOrganisationInvitation({
        projectOrganisationInvitationUuid,
        rowVersion,
      });

      if (response.status !== Status.Success || !response.data) {
        Toast.error({ message: toErrorMessage(response.errors, RESEND_ERROR_MESSAGE) });
        return;
      }

      Toast.success({ message: "You have successfully updated the project access invitation" });
      navigate(
        `${getProjectAccessDetailsRoute(
          response.data.projectOrganisationInvitationUuid,
          currentUserType,
          invitationDetails.project.uuid
        )}?${searchParams.toString()}`
      );
    }
  };

  const onCloseRevokeModal = (): void => {
    setShowRevokeModal(false);
  };

  const onOpenRevokeAccessModal = (): void => {
    setShowRevokeModal(true);
  };

  const onRevokeAccess = async (): Promise<void> => {
    setShowRevokeModal(false);
    if (invitationDetails && projectOrganisationInvitationUuid && rowVersion) {
      const response = await revokeProjectOrganisationInvitation({
        projectOrganisationInvitationUuid,
        rowVersion,
      });

      if (response.status !== Status.Success || !response.data) {
        Toast.error({ message: toErrorMessage(response.errors, REVOKE_ERROR_MESSAGE) });
        return;
      }

      Toast.success({ message: "You have successfully updated the project access invitation" });
      setInvitationDetails({ ...invitationDetails, calculatedStatus: ProjectAccessConstants.STATUS_REVOKED });
      setRowVersion(response.data.rowVersion);
    }
  };

  /**
   * Sends the access change when the selection differs from the stored role. Returns the row
   * version to carry forward, or undefined when the call failed - the caller stops there rather
   * than following a failed update with a change-RO.
   */
  const saveAccessChange = async (): Promise<number | undefined> => {
    if (!invitationDetails || !projectOrganisationInvitationUuid || !rowVersion || !accessSelection.access) {
      return undefined;
    }

    // An accepted record transfers through changeProjectResponsibleOwner instead, so it never sends
    // an intent - and .../update writes the flag on every call, so it has to be sent either way.
    const ownerIntent = isAccepted ? false : (accessSelection.makeResponsibleOwner ?? false);

    if (accessSelection.access === invitationDetails.role && ownerIntent === storedOwnerIntent) {
      return rowVersion;
    }

    const response = await updateProjectOrganisationInvitation({
      projectOrganisationInvitationUuid,
      targetRole: accessSelection.access,
      makeResponsibleOwner: ownerIntent,
      rowVersion,
    });

    if (response.status !== Status.Success || !response.data) {
      // Structured failures also land in the ErrorBox, which is where a rejected role change - the
      // one the invitee's organisation type does not allow - is worth keeping on screen.
      setErrors(response.errors);
      Toast.error({ message: toErrorMessage(response.errors, UPDATE_ACCESS_ERROR_MESSAGE) });
      return undefined;
    }

    setInvitationDetails({ ...invitationDetails, role: accessSelection.access, makeResponsibleOwner: ownerIntent });
    setRowVersion(response.data.rowVersion);
    return response.data.rowVersion;
  };

  const onSubmit = useIsLoadingWrapper(async (): Promise<void> => {
    setErrors(undefined);

    // The confirmation modal (REQ-026) owns every new RO assignment, so Save defers to it rather
    // than sending anything itself. An intent already on the record is not a new one, so editing
    // the access level beside it does not ask again.
    if (accessSelection.makeResponsibleOwner && !storedOwnerIntent) {
      setShowAssignResponsibleOwnerModal(true);
      return;
    }

    const updatedRowVersion = await saveAccessChange();

    if (updatedRowVersion !== undefined) {
      Toast.success({ message: "You have successfully updated the project access invitation" });
    }
  }, setIsOnSubmitLoading);

  const onCancelAssignResponsibleOwner = (): void => {
    setShowAssignResponsibleOwnerModal(false);
  };

  const onConfirmAssignResponsibleOwner = useIsLoadingWrapper(async (): Promise<void> => {
    if (!invitationDetails || !projectUuid) {
      setShowAssignResponsibleOwnerModal(false);
      return;
    }

    // A pending invitation has no live link to move the designation onto (BR-02864). Saving the
    // intent beside the role is what hands it over when the invitee accepts.
    if (!isAccepted) {
      const savedRowVersion = await saveAccessChange();
      setShowAssignResponsibleOwnerModal(false);

      if (savedRowVersion !== undefined) {
        Toast.success({ message: "You have successfully updated the project access invitation" });
      }
      return;
    }

    // responsibleOwner carries the row version the change is guarded by, so without it there is
    // nothing to send. A project is expected to always have one, hence the surfaced error rather
    // than a quiet no-op.
    if (!projectDetails?.responsibleOwner) {
      setShowAssignResponsibleOwnerModal(false);
      Toast.error({ message: "We could not read the project's current responsible owner. Please try again." });
      return;
    }

    // Run the access change first: for an accepted record it also updates the target's
    // project_organisation role, which is what makes a promoted guest an eligible owner
    // server-side (BR-02885). The caller's own responsible-owner link is untouched, so the row
    // version below stays valid.
    const updatedRowVersion = await saveAccessChange();

    if (updatedRowVersion === undefined) {
      setShowAssignResponsibleOwnerModal(false);
      return;
    }

    const response = await changeProjectResponsibleOwner({
      projectUuid,
      targetOrganisationUuid: invitationDetails.targetOrganisation.uuid,
      rowVersion: projectDetails.responsibleOwner.rowVersion,
    });

    setShowAssignResponsibleOwnerModal(false);

    if (response.status !== Status.Success || !response.data) {
      setErrors(response.errors);
      Toast.error({ message: toErrorMessage(response.errors, RESPONSIBLE_OWNER_ERROR_MESSAGE) });
      return;
    }

    Toast.success({ message: "You have successfully updated the project access invitation" });
    setAccessSelection((prev) => ({ ...prev, makeResponsibleOwner: false }));
    // The cached responsibleOwner is now the outgoing one and its row version is spent, so a
    // second assignment from this page would send a dead one. Refetching also drops the Make-RO
    // control, since the target now holds the role.
    shouldRefreshProjectDetails();
  }, setIsOnSubmitLoading);

  // Labelled "Close", so it leaves the page when there is anywhere to go back to. Arriving without
  // an origin param leaves no back link, and the next best thing is to drop the unsaved selection.
  const onCancel = (): void => {
    if (backLink) {
      navigate(backLink);
      return;
    }

    setAccessSelection({ access: invitationDetails?.role ?? null, makeResponsibleOwner: storedOwnerIntent });
  };

  const onAccessSelectionChange = (value: EditSelection): void => {
    setErrors(undefined);
    setAccessSelection(value);
  };

  return {
    handleBackClick,
    backLinkText,
    invitationDetails,
    organisationType: invitationDetails?.targetOrganisation.organisationType
      ? (OrganisationTypeLabels[invitationDetails.targetOrganisation.organisationType] ?? "")
      : "",
    dateSent: (invitationDetails && getFormattedDate(invitationDetails.sentDate)) ?? "",
    accessOptions,
    accessSelection,
    onAccessSelectionChange,
    onSubmit,
    onCancel,
    onResendInvitation,
    showResendInvitationModal,
    onOpenResendInvitationModal,
    onCloseResendInvitationModal,
    onRevokeAccess,
    showRevokeModal,
    onOpenRevokeAccessModal,
    onCloseRevokeModal,
    showAssignResponsibleOwnerModal,
    currentResponsibleOwnerName: projectDetails?.responsibleOwner?.displayName ?? undefined,
    onConfirmAssignResponsibleOwner,
    onCancelAssignResponsibleOwner,
    canManageAccess,
    canRevoke,
    canResend,
    showMakeResponsibleOwner,
    isOnSubmitLoading,
    isLoading,
    hasLoadError,
    errors,
  };
};
